import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import './TimelapseOverview.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const backendOrigin = (() => {
  try {
    return new URL(API_BASE_URL).origin;
  } catch {
    return API_BASE_URL;
  }
})();

function handleTimelapseAuthChallenge() {
  // Trigger a full navigation to the backend so Chrome can show the native Basic Auth popup.
  window.location.assign(`${backendOrigin}/timelapses`);
}

function formatTimeGmtPlusOne(timeString) {
  if (!timeString || !timeString.includes(':')) return '--:--';
  const [hStr, mStr] = timeString.split(':');
  const hours = Number(hStr);
  const minutes = Number(mStr);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return '--:--';
  const nextHours = (hours + 1) % 24;
  return `${String(nextHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

function TimelapseCameraCard({ cameraName, date }) {
  const [frames, setFrames] = useState([]);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchFrames = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ cameraName, date });
        const response = await fetch(`${API_BASE_URL}/api/timelapse/frames?${params.toString()}`);
        if (response.status === 401) {
          handleTimelapseAuthChallenge();
          return;
        }
        const data = await response.json();
        if (cancelled) return;
        const nextFrames = data.frames || [];
        setFrames(nextFrames);
        setIndex(nextFrames.length > 0 ? nextFrames.length - 1 : 0);
      } catch {
        if (!cancelled) {
          setFrames([]);
          setIndex(0);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    fetchFrames();
    return () => {
      cancelled = true;
    };
  }, [cameraName, date]);

  const currentFrame = frames[index];
  const imageUrl = currentFrame?.url ? `${API_BASE_URL}${currentFrame.url}` : null;

  return (
    <article className="timelapse-overview-card">
      <h3>{cameraName}</h3>
      {loading && <p className="timelapse-overview-state">Laden...</p>}
      {!loading && frames.length === 0 && (
        <p className="timelapse-overview-state">Geen frames voor deze dag.</p>
      )}
      {!loading && frames.length > 0 && (
        <>
          <div className="timelapse-overview-image-wrap">
            <img src={imageUrl} alt={`${cameraName} ${currentFrame?.time || ''}`} />
          </div>
          <div className="timelapse-overview-meta">
            <span>{formatTimeGmtPlusOne(currentFrame?.time)}</span>
            <span>{index + 1}/{frames.length}</span>
          </div>
          <input
            type="range"
            min="0"
            max={frames.length - 1}
            value={index}
            onChange={(e) => setIndex(Number(e.target.value))}
          />
        </>
      )}
    </article>
  );
}

function TimelapseOverview() {
  const [cameras, setCameras] = useState([]);
  const [activeCameraName, setActiveCameraName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [date, setDate] = useState(today);

  useEffect(() => {
    const fetchCameras = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/cameras`);
        if (!response.ok) {
          if (response.status === 401) {
            handleTimelapseAuthChallenge();
            return;
          }
          throw new Error('Failed to fetch cameras');
        }
        const data = await response.json();
        const enabledCameras = data.filter(cam => cam.enabled);
        setCameras(enabledCameras);
        if (enabledCameras.length > 0) {
          setActiveCameraName(enabledCameras[0].name);
        }
      } catch (err) {
        setError(err.message || 'Fout bij laden camera lijst');
      } finally {
        setLoading(false);
      }
    };
    fetchCameras();
  }, []);

  if (loading) {
    return (
      <div className="app-container">
        <div className="loading-screen">
          <div className="spinner"></div>
          <p>Timelapse overzicht laden...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app-container">
        <div className="error-screen">
          <h2>⚠️ Fout</h2>
          <p>{error}</p>
          <Link to="/" className="timelapse-overview-back">Terug</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <header className="app-header timelapse-overview-header">
        <Link to="/" className="back-link">← Terug naar Camera Lijst</Link>
        <h1>Dagelijks Timelapse Overzicht</h1>
        <p className="subtitle">Bekijk alle webcams voor een specifieke dag</p>
      </header>

      <div className="timelapse-overview-toolbar">
        <label htmlFor="timelapse-date">Datum</label>
        <input
          id="timelapse-date"
          type="date"
          value={date}
          max={today}
          onChange={(e) => setDate(e.target.value)}
        />
      </div>

      <nav className="timelapse-tabs" aria-label="Webcam tabs">
        {cameras.map(camera => (
          <button
            key={camera.name}
            type="button"
            className={`timelapse-tab ${activeCameraName === camera.name ? 'is-active' : ''}`}
            onClick={() => setActiveCameraName(camera.name)}
          >
            {camera.name}
          </button>
        ))}
      </nav>

      {activeCameraName ? (
        <section className="timelapse-overview-grid">
          <TimelapseCameraCard cameraName={activeCameraName} date={date} />
        </section>
      ) : (
        <p className="timelapse-overview-state">Geen webcams beschikbaar.</p>
      )}
    </div>
  );
}

export default TimelapseOverview;
